import type { EntityType, FieldDef } from '@/types/entity';

/**
 * 타입별 기본 필드 스키마 (02 §4.2). 인스펙터·시드·AI 직렬화가 공유.
 * 사용자는 entity.customFields로 개별 확장 가능.
 */
export const ENTITY_FIELDS: Record<EntityType, FieldDef[]> = {
  character: [
    { key: 'role', label: '역할', type: 'text', group: '정체', order: 0 },
    { key: 'tier', label: '티어', type: 'select', options: ['주인공', '주요', '조연'], group: '정체', order: 1 },
    { key: 'line', label: '한 줄', type: 'longtext', group: '정체', order: 2, placeholder: '이 인물을 압축하는 한 문장' },
    { key: 'wound', label: '상처', type: 'longtext', group: 'OS', order: 3 },
    { key: 'lie', label: '거짓', type: 'longtext', group: 'OS', order: 4 },
    { key: 'desire', label: '욕망', type: 'longtext', group: 'OS', order: 5 },
    { key: 'need', label: '진짜필요', type: 'longtext', group: 'OS', order: 6 },
    { key: 'flaw', label: '결함', type: 'longtext', group: 'OS', order: 7 },
    { key: 'arc', label: '아크', type: 'longtext', group: 'OS', order: 8 },
  ],
  planet: [
    { key: 'tag', label: '태그', type: 'text', group: '개요', order: 0 },
    { key: 'env', label: '환경', type: 'longtext', group: '세계', order: 1 },
    { key: 'culture', label: '문화', type: 'longtext', group: '세계', order: 2 },
    { key: 'admin', label: '행정 · 구조', type: 'longtext', group: '세계', order: 3 },
    { key: 'philosophy', label: '핵심 철학', type: 'longtext', group: '세계', order: 4 },
    { key: 'citizens', label: '시민의식', type: 'longtext', group: '세계', order: 5 },
    { key: 'cities', label: '주요 도시', type: 'longtext', group: '세계', order: 6 },
    { key: 'role', label: '서사 역할', type: 'longtext', group: '서사', order: 7 },
    { key: 'axis', label: '철학 축 (분석 0 ↔ 합일 1)', type: 'number', group: '서사', order: 8 },
  ],
  location: [
    { key: 'parent', label: '상위 행성', type: 'entityref', group: '개요', order: 0 },
    { key: 'kind', label: '유형', type: 'text', group: '개요', order: 1 },
    { key: 'desc', label: '설명', type: 'longtext', group: '개요', order: 2 },
  ],
  faction: [
    { key: 'desc', label: '설명', type: 'longtext', group: '개요', order: 0 },
    { key: 'alignment', label: '정렬', type: 'text', group: '개요', order: 1 },
    { key: 'goal', label: '목표', type: 'longtext', group: '개요', order: 2 },
  ],
  concept: [
    { key: 'definition', label: '정의', type: 'longtext', group: '개요', order: 0 },
    { key: 'themeFunction', label: '테마 기능', type: 'longtext', group: '개요', order: 1 },
  ],
  storyline: [
    { key: 'label', label: '라벨', type: 'text', group: '개요', order: 0 },
    { key: 'color', label: '색', type: 'text', group: '개요', order: 1 },
    { key: 'lead', label: '대표 인물', type: 'entityref', group: '개요', order: 2 },
  ],
  beat: [
    { key: 'storylineId', label: '스토리라인', type: 'entityref', group: '좌표', order: 0 },
    { key: 'col', label: '시점', type: 'text', group: '좌표', order: 1 },
    { key: 'order', label: '순서', type: 'number', group: '좌표', order: 2 },
    { key: 'summary', label: '요약', type: 'longtext', group: '내용', order: 3 },
    { key: 'pov', label: 'POV', type: 'text', group: '내용', order: 4 },
    { key: 'goal', label: '목표', type: 'longtext', group: '내용', order: 5 },
    { key: 'conflict', label: '갈등', type: 'longtext', group: '내용', order: 6 },
    { key: 'characters', label: '등장인물', type: 'multiselect', group: '내용', order: 7 },
    { key: 'location', label: '장소', type: 'entityref', group: '내용', order: 8 },
    { key: 'bodyDraft', label: '본문 스케치', type: 'longtext', group: '원고', order: 9 },
  ],
  theme: [
    { key: 'statement', label: '진술', type: 'longtext', group: '개요', order: 0 },
    { key: 'axis', label: '축', type: 'text', group: '개요', order: 1 },
    { key: 'basis', label: '근거', type: 'longtext', group: '개요', order: 2 },
  ],
  question: [
    { key: 'desc', label: '설명', type: 'longtext', group: '개요', order: 0 },
    { key: 'status', label: '상태', type: 'select', options: ['open', 'resolved'], group: '개요', order: 1 },
    { key: 'resolution', label: '결정 내용', type: 'longtext', group: '개요', order: 2 },
  ],
  note: [
    { key: 'bodyJSON', label: '본문', type: 'longtext', group: '개요', order: 0 },
    { key: 'linkedBeat', label: '연결 비트', type: 'entityref', group: '개요', order: 1 },
    { key: 'location', label: '장소', type: 'entityref', group: '개요', order: 2 }, // 공간 아틀라스 — 노드에서 남긴 메모 역조회
  ],
};

/** 타입의 기본 필드 + 엔티티별 커스텀 필드를 합쳐 정렬 반환 */
export function fieldsFor(type: EntityType, custom?: FieldDef[]): FieldDef[] {
  const base = ENTITY_FIELDS[type] ?? [];
  const all = [...base, ...(custom ?? [])];
  return all.slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
}

/** 새 엔티티 생성 시 기본 필드 키를 빈 값으로 초기화 */
export function emptyFields(type: EntityType): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of ENTITY_FIELDS[type] ?? []) {
    out[f.key] = f.type === 'number' ? undefined : f.type === 'multiselect' ? [] : '';
  }
  return out;
}
